// URL-backed view state for a report filter.
//
// Why this exists: a headless renderer loads a report by URL, cold, with no
// in-page clicks (EMAIL-REPORTS-PLAN §2.1). Anything held in `useState` is
// therefore invisible to it — the grower statement's date window rendered as
// the season-to-date default no matter which period the operator had chosen
// on screen. Any filter that CHANGES THE DOCUMENT has to live in the URL.
//
// It is also just better behaviour on its own terms: the URL becomes
// shareable, survives a reload, and back/forward walk the filter history.
//
//   const [from, setFrom] = useUrlState('from', seasonStartYMD())
//
// Writes are `replace: true` — dragging a date picker should not stack twenty
// history entries between the page and the button that got you there.
//
// A REGISTER keeps its filters and its open row here too (W-21): the URL is
// what Back restores, so a filter held in useState was thrown away by every
// register → record → Back round trip. Two rules make that safe:
//   - Writes COMPOSE. react-router's setSearchParams patches the params of the
//     render it was created in, so `setFrom(s); setTo(e)` in one handler lost
//     the first write. A setter patches the newest params ANY useUrlState wrote.
//   - Writes keep the router STATE. A replace without `state` deletes the
//     `returnTo` stamp (returnTo.ts, the state-clearing trap), and the page's
//     Back then goes to its default instead of where the operator came from.
//   - Writes stop at UNMOUNT. A relative `?…` navigation resolves against the
//     page that made it, so a write fired while the page is being torn down —
//     a map Popup's onClose runs as the map unmounts — replaced the page the
//     operator had just opened with this one (Scouting map → "Open this
//     observation" came straight back to the map).
// `{ elide: true }` drops the param when it is set back to its fallback, so a
// cleared register filter leaves a clean URL rather than `?from=&to=`.
import { useCallback, useLayoutEffect, useRef } from 'react'
import { useLocation, useSearchParams, type Location } from 'react-router-dom'
import { paramsPatch } from '@/lib/urlParams'

// The params the last write produced, and the location it patched. Module-
// level on purpose: `setFrom(s); setTo(e)` are two hook INSTANCES, so a ref in
// each would still let the second write patch params without the first. The
// location object is shared by every useLocation() in one render and replaced
// on every navigation, so identity says "no render since that write".
let pending: { at: Location; params: URLSearchParams } | null = null

/**
 * Read one search param as state, with a fallback when it is absent.
 *
 * `fallback` is used only when the key is missing — it is NOT elided on write
 * unless `opts.elide` asks for it. By default setting a value always writes it,
 * so a URL that has been touched is fully explicit and renders identically
 * tomorrow. (A `to` date defaulting to "today" is exactly the case that would
 * otherwise drift under a renderer.) A register's filter, whose fallback is
 * "no filter", passes `{ elide: true }`.
 *
 * `parse` narrows a raw string to the caller's union and rejects junk: a
 * hand-edited `?style=nonsense` must fall back, not render a broken document.
 */
export function useUrlState<T extends string = string>(
  key: string,
  // NoInfer, or `useUrlState('to', '2026-08-14')` infers T as the literal
  // '2026-08-14' and the setter then accepts only that one date.
  fallback: NoInfer<T>,
  parse?: (raw: string) => T | null,
  opts: { elide?: boolean } = {},
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  // Layout phase: a deleted tree's layout cleanups run parent-first, so this
  // flips before any child's teardown can call the setter.
  const mounted = useRef(true)
  useLayoutEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const raw = params.get(key)
  const parsed = raw == null ? null : parse ? parse(raw) : (raw as T)
  const value = parsed ?? fallback

  const elide = !!opts.elide
  const set = useCallback((next: T) => {
    if (!mounted.current) return
    const base = pending?.at === location ? pending.params : new URLSearchParams(location.search)
    const patched = paramsPatch(base, { [key]: elide && next === fallback ? null : next })
    pending = { at: location, params: patched }
    setParams(patched, { replace: true, state: location.state })
  }, [key, setParams, location, elide, fallback])

  return [value, set]
}

/**
 * Write SEVERAL params in one replace — a tab switch that also clears the
 * filters and open record of the tab it leaves, or a tile that opens a tab on
 * a filter (`{ tab: 'checklists', runs: 'overdue' }`). Same rules as a
 * useUrlState setter: it composes with them in one handler (shares `pending`),
 * keeps the router state, and stops at unmount. `null` deletes a key.
 */
export function useUrlPatch(): (patch: Record<string, string | null>) => void {
  const [, setParams] = useSearchParams()
  const location = useLocation()
  const mounted = useRef(true)
  useLayoutEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  return useCallback((patch: Record<string, string | null>) => {
    if (!mounted.current) return
    const base = pending?.at === location ? pending.params : new URLSearchParams(location.search)
    const patched = paramsPatch(base, patch)
    pending = { at: location, params: patched }
    setParams(patched, { replace: true, state: location.state })
  }, [setParams, location])
}

/** `parse` for a fixed set of allowed values. */
export function oneOf<T extends string>(...allowed: readonly T[]) {
  return (raw: string): T | null => (allowed as readonly string[]).includes(raw) ? (raw as T) : null
}

/** `parse` for a YYYY-MM-DD date. Rejects anything else, so a malformed date
 *  falls back to the caller's default rather than reaching a date library. */
export function ymdParam(raw: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null
}
